/**
 * Calendar Page - Separate route for event schedule
 * 
 * Accessed via:
 * - Explore section entry point
 * - Hamburger menu navigation
 * 
 * GATED: Only accessible when PHASE_2_RELEASE=true
 * Structure only - no integrations yet
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, MapPin, Download } from 'lucide-react';
import TOKENS from '../data/tokens';
import PreviewBanner from '../components/PreviewBanner';
import MaintenanceScreen from '../components/MaintenanceScreen';
import { isMaintenanceMode } from '../utils/featureGates';

// Event date
const EVENT_DATE = new Date('2026-07-25T18:00:00-04:00');

const CalendarPage = () => {
  const navigate = useNavigate();
  
  // MAINTENANCE MODE CHECK
  if (isMaintenanceMode()) {
    return <MaintenanceScreen />;
  }
  
  const formattedDate = EVENT_DATE.toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  
  const formattedTime = EVENT_DATE.toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  });
  
  // Generate Google Calendar link
  const generateGoogleCalendarLink = () => {
    const title = encodeURIComponent('80 Years of Connection Celebration');
    const details = encodeURIComponent('Join us for this special celebration marking 80 years of connection.');
    const location = encodeURIComponent('Venue TBA');
    const startDate = '20260725T220000Z';
    const endDate = '20260726T020000Z';
    
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&details=${details}&location=${location}&dates=${startDate}/${endDate}`;
  };
  
  // Check if preview banner will be shown (to add padding)
  const isPreview = window.location.hostname.includes('preview.emergentagent.com') ||
                    window.location.hostname.includes('localhost') ||
                    window.location.hostname.includes('127.0.0.1');
  
  return (
    <div style={{ 
      minHeight: '100vh',
      background: TOKENS.colors.heroBackground,
      paddingTop: isPreview ? 48 : 0, // Account for preview banner height
    }}>
      <PreviewBanner />
      
      {/* Simple header with back button */}
      <div style={{
        padding: '16px 24px',
        borderBottom: `1px solid ${TOKENS.colors.borderGrey}`,
        background: TOKENS.colors.cardBackground,
        display: 'flex',
        alignItems: 'center',
        gap: 16,
      }}>
        <button
          onClick={() => navigate('/', { state: { bypassSplash: true } })}
          style={{
            background: 'rgba(212, 168, 83, 0.1)',
            border: '1px solid rgba(212, 168, 83, 0.3)',
            borderRadius: 24,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 18px',
            color: TOKENS.colors.textGold,
            fontSize: 14,
            fontWeight: 600,
            transition: 'all 0.2s ease',
          }}
          data-testid="calendar-back-btn"
        >
          <ArrowLeft size={18} />
          <span>Return</span>
        </button>
      </div>
      
      {/* Page Content */}
      <div style={{ padding: '40px 24px', maxWidth: 600, margin: '0 auto' }}>
        {/* Page Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            width: 64,
            height: 64,
            background: '#FFF3E0',
            border: `1px solid ${TOKENS.colors.borderGrey}`,
            borderRadius: TOKENS.borderRadius.xl,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <Calendar size={28} color={TOKENS.colors.textGold} />
          </div>
          
          <div style={{ 
            fontSize: TOKENS.fontSizes.labelSmall, 
            fontWeight: TOKENS.fontWeights.bold,
            color: TOKENS.colors.textGoldDark, 
            letterSpacing: TOKENS.letterSpacing.wide, 
            marginBottom: 8,
            textTransform: 'uppercase'
          }}>
            MARK YOUR CALENDAR
          </div>
          <h1 style={{ 
            fontFamily: TOKENS.fonts.heading, 
            fontSize: 28, 
            fontWeight: TOKENS.fontWeights.medium, 
            color: TOKENS.colors.textBlack, 
            margin: 0,
          }}>
            Event Schedule
          </h1>
        </div>
        
        {/* Main Event Card */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(212,168,83,0.6), rgba(198,167,90,0.25), rgba(212,168,83,0.6))',
          padding: 3,
          borderRadius: 16,
          boxShadow: '0 16px 48px rgba(0, 0, 0, 0.12), 0 8px 24px rgba(212, 168, 83, 0.18)',
        }}>
          <div style={{ 
            background: TOKENS.colors.cardBackground, 
            borderRadius: 13, 
            padding: 24, 
          }}>
            {/* Event Title */}
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={{ 
                fontFamily: TOKENS.fonts.heading, 
                fontSize: 22, 
                fontWeight: TOKENS.fontWeights.semiBold, 
                color: TOKENS.colors.textBlack, 
                marginBottom: 8 
              }}>
                80 Years of Connection
              </div>
              <div style={{
                display: 'inline-block',
                background: 'linear-gradient(135deg, rgba(212,168,83,0.15) 0%, rgba(232,164,184,0.10) 100%)',
                borderRadius: 20,
                padding: '6px 16px',
              }}>
                <span style={{ 
                  fontSize: 11, 
                  fontWeight: TOKENS.fontWeights.medium, 
                  color: TOKENS.colors.textGold,
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                }}>
                  Birthday Celebration
                </span>
              </div>
            </div>
            
            {/* Date & Time Info */}
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 12,
              marginBottom: 24,
            }}>
              <InfoRow icon={Calendar} label={formattedDate} />
              <InfoRow icon={Clock} label={formattedTime} sublabel="Evening Celebration" />
              <InfoRow icon={MapPin} label="Venue Details Coming Soon" sublabel="You'll receive the address with your invitation" />
            </div>
            
            {/* Add to Calendar Button */}
            <a 
              href={generateGoogleCalendarLink()}
              target="_blank"
              rel="noopener noreferrer"
              style={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                width: '100%',
                padding: '14px 24px',
                background: `linear-gradient(135deg, ${TOKENS.colors.buttonGold} 0%, ${TOKENS.colors.buttonGoldHover} 100%)`,
                color: TOKENS.colors.textWhite,
                border: 'none',
                borderRadius: 10,
                fontSize: 15,
                fontWeight: TOKENS.fontWeights.semiBold,
                cursor: 'pointer',
                textDecoration: 'none',
                boxShadow: TOKENS.shadows.button,
              }}
              data-testid="add-to-calendar-btn"
            >
              <Download size={18} />
              Add to Google Calendar
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

const InfoRow = ({ icon: Icon, label, sublabel }) => (
  <div style={{ 
    display: 'flex', 
    alignItems: 'center', 
    gap: 12,
    padding: '12px 16px',
    background: 'linear-gradient(135deg, rgba(212,168,83,0.06) 0%, rgba(232,164,184,0.04) 100%)',
    borderRadius: 10,
  }}>
    <Icon size={20} color={TOKENS.colors.textGold} />
    <div>
      <div style={{ 
        fontSize: 15, 
        fontWeight: TOKENS.fontWeights.semiBold, 
        color: TOKENS.colors.textBlack 
      }}>
        {label}
      </div>
      {sublabel && (
        <div style={{ 
          fontSize: 13, 
          color: TOKENS.colors.textGrey 
        }}>
          {sublabel}
        </div>
      )}
    </div>
  </div>
);

export default CalendarPage;
